import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/content';
import supabase from '../supabase/supabase';

const { FiMail, FiPhone, FiMapPin, FiSend, FiCheckCircle } = FiIcons;

const Contact = () => {
  const { lang } = useLanguage();
  const t = translations[lang].contact;
  
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    gdpr: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formState.gdpr) return;
    
    setIsSubmitting(true);
    setError(null);

    try {
      // Updated to match the lowercase table name in database
      const { error: dbError } = await supabase
        .from('contact_form_1768669379138')
        .insert([
          {
            name: formState.name,
            email: formState.email,
            subject: formState.subject,
            message: formState.message
          }
        ]);

      if (dbError) throw dbError;

      setIsSubmitting(false);
      setSubmitted(true);
      setFormState({ name: '', email: '', subject: '', message: '', gdpr: false });
    } catch (err) {
      console.error('Submission error:', err);
      setError(lang === 'de' ? 'Fehler beim Senden. Bitte versuchen Sie es später erneut.' : 'Error sending message. Please try again later.');
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="bg-gray-50 py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-extrabold text-gray-900 sm:text-5xl"
          >
            {t.title}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-xl text-gray-600 max-w-2xl mx-auto"
          >
            {t.subtitle}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-xl p-8 sm:p-10 border border-gray-100"
          >
            {submitted ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
                  <SafeIcon icon={FiCheckCircle} className="text-3xl text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t.success}</h3>
                <button 
                  onClick={() => setSubmitted(false)}
                  className="mt-6 text-blue-600 font-semibold hover:underline"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t.name}</label>
                    <input
                      required
                      type="text"
                      name="name"
                      value={formState.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t.email}</label>
                    <input
                      required
                      type="email"
                      name="email"
                      value={formState.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t.subject}</label>
                  <input
                    required
                    type="text"
                    name="subject"
                    value={formState.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t.message}</label>
                  <textarea
                    required
                    rows="4"
                    name="message"
                    value={formState.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none resize-none"
                  ></textarea>
                </div>
                
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      required
                      id="gdpr"
                      name="gdpr"
                      type="checkbox"
                      checked={formState.gdpr}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="gdpr" className="text-gray-600 cursor-pointer">
                      {t.gdpr}
                    </label>
                  </div>
                </div>

                {error && (
                  <p className="text-red-500 text-sm bg-red-50 p-2 rounded">{error}</p>
                )}

                <button
                  disabled={isSubmitting || !formState.gdpr}
                  type="submit"
                  className={`w-full flex items-center justify-center space-x-2 py-4 px-6 rounded-xl font-bold text-white transition-all transform active:scale-95 ${
                    isSubmitting || !formState.gdpr
                      ? 'bg-gray-400 cursor-not-allowed opacity-50'
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:shadow-lg hover:-translate-y-0.5'
                  }`}
                >
                  <SafeIcon icon={FiSend} />
                  <span>{isSubmitting ? (lang === 'de' ? 'Wird gesendet...' : 'Sending...') : t.submit}</span>
                </button>
              </form>
            )}
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-8"
          >
            <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-8">{t.infoTitle}</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiMail} className="text-xl text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t.emailLabel}</p>
                    <p className="text-lg font-bold text-gray-900" style={{ color: 'rgb(17, 24, 39)' }}>hello@stup-on.de</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiPhone} className="text-xl text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Call us</p>
                    <p style={{
      color: 'rgb(17, 24, 39)'
    }} className="text-lg font-bold text-gray-900">+49 (0) 151 15 250963</p>
                    <p className="text-sm text-gray-500">{t.workingHours}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiMapPin} className="text-xl text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t.locationLabel}</p>
                    <p className="text-lg font-bold text-gray-900">Berlin, Germany</p>
                    <p className="text-sm text-gray-500">Innovation Hub, Street 123</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl p-8 text-white shadow-xl">
              <h4 className="text-xl font-bold mb-4">Why reach out?</h4>
              <ul className="space-y-3 opacity-90">
                <li className="flex items-center space-x-2">
                  <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
                  <span>Free initial consultation</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
                  <span>Clarify step-by-step logic</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
                  <span>Custom roadmap advice</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;